import {CSSProperties} from 'react'
import {SETTINGS_OF_PLATFORM} from 'src/interfaces/Platform.interface'

export type MappingParentFields = {
  belongs_to_id?: number | string | null
  mapping_parent?: number | string | null
  parent?: number | string | null
  setting_mapping_id?: number | string | null
  id?: number | string | null
  input_type?: string | null
}

const toFiniteMappingId = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const id = Number(value)
  return Number.isFinite(id) ? id : null
}

export const getMappingOwnId = (
  mapping: MappingParentFields | null | undefined
): number | null =>
  toFiniteMappingId(mapping?.setting_mapping_id) ?? toFiniteMappingId(mapping?.id)

const isBooleanMapping = (mapping: MappingParentFields | null | undefined): boolean =>
  `${mapping?.input_type ?? ''}`.toUpperCase() === 'BOOLEAN'

type MappingParentGroup<T> = {
  parentEntries: T[]
  childrenByParentId: Map<number, T[]>
}

/** Sort-only nesting: belongs_to_id first, then mapping_parent/parent on BOOLEAN parents. */
const groupMappingsByParent = <T,>(
  entries: T[],
  getMapping: (entry: T) => MappingParentFields | null | undefined
): MappingParentGroup<T> => {
  const booleanParentIds = new Set<number>()

  entries.forEach((entry) => {
    const mapping = getMapping(entry)
    const ownId = getMappingOwnId(mapping)
    if (ownId !== null && isBooleanMapping(mapping)) {
      booleanParentIds.add(ownId)
    }
  })

  const childrenByParentId = new Map<number, T[]>()
  const parentEntries: T[] = []

  entries.forEach((entry) => {
    const mapping = getMapping(entry)
    const belongsToId = toFiniteMappingId(mapping?.belongs_to_id)

    if (belongsToId !== null) {
      const existing = childrenByParentId.get(belongsToId) ?? []
      childrenByParentId.set(belongsToId, [...existing, entry])
      return
    }

    const declaredParentId =
      toFiniteMappingId(mapping?.mapping_parent) ?? toFiniteMappingId(mapping?.parent)

    if (declaredParentId !== null && booleanParentIds.has(declaredParentId)) {
      const existing = childrenByParentId.get(declaredParentId) ?? []
      childrenByParentId.set(declaredParentId, [...existing, entry])
      return
    }

    parentEntries.push(entry)
  })

  return {parentEntries, childrenByParentId}
}

export const CHANNEL_SETTING_ADVANCED_SEQUENCE_ORDER = 1_000_000

export type SettingNode = Omit<
  SETTINGS_OF_PLATFORM,
  'children' | 'field_mappings' | 'mappings_data'
> & {
  position?: number | string | null
  children?: SettingNode[]
  field_mappings?: MappingNode[]
  mappings_data?: MappingNode[]
}

export type MappingNode = MappingParentFields & {
  position?: number | string | null
  mapping_position?: number | string | null
  api_code?: string | null
  field_code?: string | null
  isDefault?: boolean | null
  is_advance_setting?: boolean | null
}

export type PageSiblingKind = 'child' | 'mapping'
export type PageSiblingSource = 'this' | 'other'

export type PageSibling = {
  kind: PageSiblingKind
  source: PageSiblingSource
  item: SettingNode | MappingNode
  collectIndex: number
  sortKey: number
}

const isIdentifierSection = (setting: SettingNode | null | undefined): boolean =>
  `${setting?.group_code ?? ''}`.toUpperCase() === 'IDENTIFIER'

/** Finite number including 0. Empty / NaN / non-numeric → missing (last). */
export const toSortKey = (value: unknown): number => {
  if (value === undefined || value === null || value === '') {
    return Number.POSITIVE_INFINITY
  }

  const position = Number(value)
  return Number.isFinite(position) ? position : Number.POSITIVE_INFINITY
}

export const getSettingPosition = (setting: SettingNode | null | undefined): number =>
  toSortKey(setting?.position)

export const getMappingPosition = (mapping: MappingNode | null | undefined): number =>
  toSortKey(mapping?.mapping_position)

export const isAdvancedSettingItem = (
  item: SettingNode | MappingNode | null | undefined
): boolean => Boolean((item as {is_advance_setting?: boolean | null})?.is_advance_setting)

/** Advanced children/mappings stay in the accordion, not the unified sequence row. */
export const shouldIncludeInSequenceRow = (
  item: SettingNode | MappingNode | null | undefined
): boolean => !isAdvancedSettingItem(item)

/**
 * Identity for a real pair (isDefault false). Independent defaults stay separate dropdowns.
 * Same api_code on both channels shares this identity. Same-field slots with different
 * map_with targets are split in collapsePairedMappingSiblings by setting_mapping_id, not here.
 */
export const getMappingPairIdentity = (mapping: MappingNode | null | undefined): string | null => {
  if (mapping?.isDefault === true) {
    return null
  }

  const code = `${mapping?.api_code || mapping?.field_code || ''}`.trim()
  if (!code) {
    return null
  }

  return `${code}::false`
}

const compareKeyThenIndex = (leftKey: number, leftIndex: number, rightKey: number, rightIndex: number) =>
  leftKey - rightKey || leftIndex - rightIndex

const sortEntries = <T,>(
  items: T[],
  getKey: (item: T) => number
): T[] =>
  items
    .map((item, index) => ({item, index}))
    .sort((left, right) => compareKeyThenIndex(getKey(left.item), left.index, getKey(right.item), right.index))
    .map(({item}) => item)

export const getTopLevelMappings = <T extends MappingNode>(mappings: T[] = []): T[] => {
  const grouped = groupMappingsByParent(mappings, (mapping) => mapping)
  return grouped.parentEntries
}

const sortMappingGroup = <T extends MappingNode>(entries: T[]): T[] =>
  sortEntries(entries, getMappingPosition)

/** Nest first, then sort each sibling group. Does not drop leftover nested rows. */
export const sortMappingsByPosition = <T extends MappingNode>(mappings: T[] = []): T[] => {
  if (!mappings.length) {
    return []
  }

  const grouped = groupMappingsByParent(mappings, (mapping) => mapping)
  const sortedParents = sortMappingGroup(grouped.parentEntries)
  const emitted = new Set<T>()
  const ordered: T[] = []

  sortedParents.forEach((parent) => {
    ordered.push(parent)
    emitted.add(parent)

    const parentId = getMappingOwnId(parent)
    const children = parentId === null ? [] : grouped.childrenByParentId.get(parentId) ?? []
    sortMappingGroup(children).forEach((child) => {
      ordered.push(child)
      emitted.add(child)
    })
  })

  mappings.forEach((mapping) => {
    if (!emitted.has(mapping)) {
      ordered.push(mapping)
    }
  })

  return ordered
}

const sortSettingNode = (setting: SettingNode): SettingNode => ({
  ...setting,
  children: sortSettingChildren(setting.children ?? []),
  mappings_data: sortMappingsByPosition(setting.mappings_data ?? []),
  field_mappings: sortMappingsByPosition(setting.field_mappings ?? []),
})

const sortSettingChildren = (children: SettingNode[] = []): SettingNode[] =>
  sortEntries(
    children.map((child) => sortSettingNode(child)),
    getSettingPosition
  )

const sortRootSections = (settings: SettingNode[]): SettingNode[] => {
  const identifierSections: SettingNode[] = []
  const otherSections: Array<{setting: SettingNode; index: number}> = []

  settings.forEach((setting, index) => {
    if (isIdentifierSection(setting)) {
      identifierSections.push(setting)
      return
    }

    otherSections.push({setting, index})
  })

  otherSections.sort((left, right) =>
    compareKeyThenIndex(
      getSettingPosition(left.setting),
      left.index,
      getSettingPosition(right.setting),
      right.index
    )
  )

  return [...identifierSections, ...otherSections.map(({setting}) => setting)]
}

const sortChannelSettingsCatalogInternal = (
  settings: SETTINGS_OF_PLATFORM[] | SettingNode[] | null | undefined = []
): SETTINGS_OF_PLATFORM[] => {
  if (!Array.isArray(settings) || settings.length === 0) {
    return []
  }

  return sortRootSections(
    (settings as SettingNode[]).map((setting) => sortSettingNode(setting))
  ) as SETTINGS_OF_PLATFORM[]
}

/** Immutable catalog sort: identifier first, then each node’s children + mappings. */
export const sortChannelSettingsCatalog = (
  settings: SETTINGS_OF_PLATFORM[] | SettingNode[] | null | undefined = []
): SETTINGS_OF_PLATFORM[] => sortChannelSettingsCatalogInternal(settings)

export const getChannelSettingAdvancedSequenceOrderStyle = (): CSSProperties | undefined => ({
  order: CHANNEL_SETTING_ADVANCED_SEQUENCE_ORDER,
})

export const getPageSiblingKey = (
  kind: PageSiblingKind,
  source: PageSiblingSource,
  item: SettingNode | MappingNode | null | undefined
): string => {
  if (kind === 'child') {
    const child = item as SettingNode
    return `child:${source}:${child?.id ?? child?.name ?? ''}`
  }

  const mapping = item as MappingNode
  const ownId = getMappingOwnId(mapping)
  const pairIdentity = getMappingPairIdentity(mapping)
  return `mapping:${source}:${ownId ?? pairIdentity ?? ''}`
}

export const buildPageSiblings = ({
  thisSetting,
  extraChildren = [],
  otherSetting,
}: {
  thisSetting: SettingNode | null | undefined
  extraChildren?: SettingNode[]
  otherSetting?: SettingNode | null
}): PageSibling[] => {
  const siblings: PageSibling[] = []
  const thisPairKeys = new Map<string, number>()

  const pushSibling = (
    kind: PageSiblingKind,
    source: PageSiblingSource,
    item: SettingNode | MappingNode,
    sortKey: number
  ) => {
    siblings.push({
      kind,
      source,
      item,
      collectIndex: siblings.length,
      sortKey,
    })
  }

  ;(thisSetting?.children ?? []).forEach((child) => {
    if (!shouldIncludeInSequenceRow(child)) {
      return
    }

    pushSibling('child', 'this', child, getSettingPosition(child))
  })

  extraChildren.forEach((child) => {
    if (!shouldIncludeInSequenceRow(child)) {
      return
    }

    pushSibling('child', 'other', child, getSettingPosition(child))
  })

  getTopLevelMappings(thisSetting?.mappings_data ?? []).forEach((mapping) => {
    if (!shouldIncludeInSequenceRow(mapping)) {
      return
    }

    const sortKey = getMappingPosition(mapping)
    const pairIdentity = getMappingPairIdentity(mapping)
    if (pairIdentity) {
      thisPairKeys.set(pairIdentity, sortKey)
    }
    pushSibling('mapping', 'this', mapping, sortKey)
  })

  getTopLevelMappings(otherSetting?.mappings_data ?? []).forEach((mapping) => {
    if (!shouldIncludeInSequenceRow(mapping)) {
      return
    }

    const pairIdentity = getMappingPairIdentity(mapping)
    const thisSideKey = pairIdentity ? thisPairKeys.get(pairIdentity) : undefined
    pushSibling(
      'mapping',
      'other',
      mapping,
      thisSideKey === undefined ? getMappingPosition(mapping) : thisSideKey
    )
  })

  return siblings.sort(
    (left, right) =>
      compareKeyThenIndex(left.sortKey, left.collectIndex, right.sortKey, right.collectIndex)
  )
}

export const buildPageSiblingRankMap = (args: {
  thisSetting: SettingNode | null | undefined
  extraChildren?: SettingNode[]
  otherSetting?: SettingNode | null
}): Map<string, number> => {
  const ranks = new Map<string, number>()

  buildPageSiblings(args).forEach((sibling, index) => {
    ranks.set(getPageSiblingKey(sibling.kind, sibling.source, sibling.item), index + 1)
  })

  return ranks
}

export const getPageSiblingRank = (
  ranks: Map<string, number>,
  kind: PageSiblingKind,
  source: PageSiblingSource,
  item: SettingNode | MappingNode | null | undefined
): number | undefined => ranks.get(getPageSiblingKey(kind, source, item))
